// The finale: night falls, fireworks and the thank-you card.
import * as THREE from 'three';
import { PI } from '../lib/math.js';
import { pick, rnd, rr } from '../lib/random.js';
import { cam, controls, scene } from '../core/scene.js';
import { setTilt } from '../core/post.js';
import { canvasTex } from '../lib/factory.js';
import { S, startRide } from './ride.js';
import { setMode } from './daynight.js';
import { resetGame } from './stop-panel.js';
import { state } from '../core/state.js';
import { chapters, hint, speed } from '../dom.js';

export let finaleSeen, finaleWasNight, startFinale, endFinale, finaleCam, updateFireworks;

export function buildFinale() {
  startFinale = function startFinale() {
    finaleSeen = true;
    finaleWasNight = state.isNight;
    setMode(true);
    state.mode = 'finale';
    controls.enabled = false;
    document.body.classList.remove('riding');
    setTilt(0.5, 2.5);
    chapters.style.display = 'none';
    speed.style.display = 'none';
    hint.style.display = 'none';
    cam.fov = 50;
    cam.updateProjectionMatrix();
    fw.on = true;
    fw.timer = 0;
    const t = FIN[state.lang];
    const q = (sel) => finaleEl.querySelector(sel);
    q('.k').textContent = t.k;
    q('h2').textContent = t.h;
    q('.p1').textContent = t.p1;
    q('.p2').textContent = t.p2;
    q('.li span').textContent = t.li;
    q('.ride').textContent = t.ride;
    q('.again').textContent = t.again;
    setTimeout(() => {
      finaleEl.classList.add('open');
      q('.li').focus({ preventScroll: true });
    }, 1400); // let the first rockets go up first
  };
  endFinale = function endFinale(again) {
    finaleEl.classList.remove('open');
    fw.on = false;
    hint.style.display = '';
    setMode(finaleWasNight);
    if (again) {
      resetGame(false);
      finaleSeen = false;
      return;
    }
    chapters.style.display = 'block';
    speed.style.display = 'block';
    controls.target.set(S.x, S.y + 1.4, S.z);
    startRide();
  };
  // a slow wide shot from over the sea, looking across the city at the sky where the fireworks burst
  finaleCam = function finaleCam(time) {
    cam.position.set(Math.sin(time * 0.05) * 18, 26, 100);
    cam.lookAt(0, 24, 0);
  };
  updateFireworks = function updateFireworks(dt) {
    if (fw.on) {
      fw.timer -= dt;
      if (fw.timer <= 0 && fw.list.length < 16) {
        launchRocket();
        if (rnd() < 0.45) launchRocket();
        fw.timer = rr(0.35, 0.8);
      }
    }
    for (let k = fw.list.length - 1; k >= 0; k--) {
      const f = fw.list[k];
      f.t += dt;
      if (f.phase === 'rise') {
        // all sparks travel together as the rocket head, with a short trail behind it
        const u = Math.min(1, f.t / 1.3),
          e = 1 - (1 - u) * (1 - u);
        for (let i = 0; i < f.N; i++) {
          const lag = (i % 12) * 0.007;
          const ee = Math.max(0, e - lag);
          f.pos.set([f.start.x, f.start.y + (f.top.y - f.start.y) * ee, f.start.z], i * 3);
        }
        f.mat.size = 1.4;
        f.mat.opacity = 1;
        if (u >= 1) {
          f.phase = 'burst';
          f.t = 0;
          for (let i = 0; i < f.N; i++) f.pos.set([f.top.x, f.top.y, f.top.z], i * 3);
        }
      } else {
        const drag = Math.exp(-1.6 * dt);
        for (let i = 0; i < f.N; i++) {
          f.vel[i * 3] *= drag;
          f.vel[i * 3 + 1] = f.vel[i * 3 + 1] * drag - 5 * dt;
          f.vel[i * 3 + 2] *= drag;
          f.pos[i * 3] += f.vel[i * 3] * dt;
          f.pos[i * 3 + 1] += f.vel[i * 3 + 1] * dt;
          f.pos[i * 3 + 2] += f.vel[i * 3 + 2] * dt;
        }
        f.mat.size = 2.4;
        f.mat.opacity = Math.max(0, 1 - f.t / 2.6) * (0.8 + 0.2 * Math.sin(f.t * 40));
        if (f.t > 2.6) {
          scene.remove(f.pts);
          f.geo.dispose();
          f.mat.dispose();
          fw.list.splice(k, 1);
          continue;
        }
      }
      f.geo.attributes.position.needsUpdate = true;
    }
  };
  // ---------- the finale: after the last chapter is closed, night falls over Barcelona, fireworks go up over the
  // city and a thank-you card invites the visitor to get in touch ----------
  finaleSeen = false;
  finaleWasNight = false;
  const finaleEl = document.getElementById('finale');
  const FIN = {
    es: {
      k: 'Fin del recorrido',
      h: '¡Gracias por el paseo!',
      p1: 'Has recorrido toda mi trayectoria: de mis primeros módulos de ERP en Aradoxa a liderar producto e ingeniería en Welocalize. Gracias por dedicarle tu tiempo; espero que te haya gustado.',
      p2: 'Si crees que puedo encajar en tu equipo, quieres contrastar una idea de producto o simplemente saludar, escríbeme. Me encantará hablar contigo.',
      li: 'Conectemos en LinkedIn',
      ride: 'Seguir paseando',
      again: 'Volver a empezar',
    },
    en: {
      k: 'End of the ride',
      h: 'Thanks for riding along!',
      p1: 'You’ve travelled my whole career: from my first ERP modules at Aradoxa to leading product and engineering at Welocalize. Thank you for taking the time; I hope you enjoyed it.',
      p2: 'If you think I could fit in your team, want to bounce a product idea around or just say hi, drop me a line. I’d love to talk.',
      li: 'Let’s connect on LinkedIn',
      ride: 'Keep riding',
      again: 'Start over',
    },
  };
  finaleEl.querySelector('.ride').onclick = () => endFinale(false);
  finaleEl.querySelector('.again').onclick = () => endFinale(true);
  // fireworks: each rocket climbs on a thin trail, then bursts into a sphere of sparks that fall, drag and fade
  const fw = { on: false, timer: 0, list: [] };
  const sparkTex = canvasTex(64, 64, (c) => {
    const g = c.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.25, 'rgba(255,255,255,.8)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    c.fillStyle = g;
    c.fillRect(0, 0, 64, 64);
  });
  sparkTex.wrapS = sparkTex.wrapT = THREE.ClampToEdgeWrapping;
  const FW_COLS = [
    [1, 0.35, 0.3],
    [1, 0.8, 0.25],
    [0.4, 0.75, 1],
    [0.6, 1, 0.5],
    [1, 0.5, 0.9],
    [1, 1, 1],
    [0.75, 0.5, 1],
  ];
  function launchRocket() {
    const N = 240,
      pos = new Float32Array(N * 3),
      col = new Float32Array(N * 3),
      geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    const mat = new THREE.PointsMaterial({
      size: 1.1,
      map: sparkTex,
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      fog: false,
    });
    const pts = new THREE.Points(geo, mat);
    pts.frustumCulled = false;
    scene.add(pts);
    const start = new THREE.Vector3(rr(-70, 70), 0, rr(-40, 20)),
      top = start.clone().setY(rr(42, 66));
    const c1 = pick(FW_COLS),
      c2 = rnd() < 0.4 ? pick(FW_COLS) : c1,
      vel = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      const u = rnd() * 2 - 1,
        a = rnd() * PI * 2,
        r = Math.sqrt(1 - u * u),
        sp = rr(15, 19) * (i % 5 ? 1 : 0.55);
      vel.set([Math.cos(a) * r * sp, u * sp, Math.sin(a) * r * sp], i * 3);
      col.set(i % 2 ? c1 : c2, i * 3);
    }
    fw.list.push({ pts, geo, mat, pos, vel, N, start, top, t: 0, phase: 'rise' });
  }
}
